/**
 * グループに配信先を追加するためのピッカー（複数選択可）。
 * 仕様書: docs/01_spec/spec_mail_system.md B-11.3
 */
import { Search, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMailContacts } from '@/hooks/useMailContacts';
import { useAddMembersToGroup } from '@/hooks/useMailGroups';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  existingMemberIds: string[];
}

export function MemberPickerDialog({ open, onOpenChange, groupId, existingMemberIds }: Props) {
  const { data: contacts, isLoading } = useMailContacts({ limit: 5000 });
  const addMembers = useAddMembersToGroup();
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ダイアログを開いたときに選択状態をリセット
  useEffect(() => {
    if (open) {
      setKeyword('');
      setSelected(new Set());
    }
  }, [open]);

  const candidates = useMemo(() => {
    const existingSet = new Set(existingMemberIds);
    const list = (contacts ?? []).filter((c) => !existingSet.has(c.id));
    const k = keyword.trim().toLowerCase();
    if (!k) return list;
    return list.filter((c) => {
      if (c.name?.toLowerCase().includes(k)) return true;
      if (c.emails?.some((e) => e.address.toLowerCase().includes(k))) return true;
      if (c.tags?.some((t) => t.toLowerCase().includes(k))) return true;
      return false;
    });
  }, [contacts, existingMemberIds, keyword]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of candidates) next.add(c.id);
      return next;
    });
  };

  const clearAll = () => setSelected(new Set());

  const handleAdd = async () => {
    if (selected.size === 0) return;
    try {
      await addMembers.mutateAsync({
        groupId,
        contactIds: Array.from(selected),
      });
      toast.success(`${selected.size} 名をグループに追加しました`);
      onOpenChange(false);
    } catch (err) {
      toast.error(`追加失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>メンバー追加</DialogTitle>
          <DialogDescription>
            グループに追加する配信先を選択してください。既に登録済みの配信先は表示されません。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
            <Input
              type="search"
              placeholder="名前 / メアド / タグで検索"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
            <span>
              {isLoading ? '読み込み中…' : `${candidates.length} 件 / 選択 ${selected.size} 件`}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAllVisible}
                disabled={candidates.length === 0}
                className="text-[var(--accent)] hover:underline disabled:opacity-40 disabled:no-underline"
              >
                表示中を全選択
              </button>
              <button
                type="button"
                onClick={clearAll}
                disabled={selected.size === 0}
                className="text-[var(--text-secondary)] hover:underline disabled:opacity-40 disabled:no-underline"
              >
                選択解除
              </button>
            </div>
          </div>

          <div className="border border-[var(--border)] rounded max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <Spinner />
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-xs text-[var(--text-muted)] italic text-center py-12">
                {keyword ? '一致する配信先がありません' : '追加可能な配信先がありません'}
              </div>
            ) : (
              <div className="divide-y divide-[var(--border)]">
                {candidates.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-[var(--bg-hover)] cursor-pointer text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      className="size-3.5 accent-[var(--accent)]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{c.name || '（名前なし）'}</div>
                      <div className="text-[var(--text-muted)] font-mono">{c.defaultEmail}</div>
                    </div>
                    {c.tags && c.tags.length > 0 && (
                      <div className="flex gap-1 flex-shrink-0">
                        {c.tags.slice(0, 3).map((t) => (
                          <span
                            key={t}
                            className="text-[10px] bg-[var(--bg-hover)] px-1.5 py-0.5 rounded-full text-[var(--text-muted)]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleAdd}
            disabled={selected.size === 0 || addMembers.isPending}
          >
            {addMembers.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <UserPlus className="size-3.5 mr-1" />
            )}
            {selected.size > 0 ? `${selected.size} 名を追加` : 'メンバー追加'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
